class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('.predictive-search');

    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    // Get both API results and CSV results
    Promise.all([
      this.getAPIResults(searchTerm),
      this.getCSVResults(searchTerm)
    ]).then(([apiResults, csvResults]) => {
      this.mergeAndDisplayResults(apiResults, csvResults, searchTerm);
    }).catch((error) => {
      console.error('Search error:', error);
      // Fallback to just API results if CSV fails
      this.getAPIResults(searchTerm).then((apiResults) => {
        this.predictiveSearchResults.innerHTML = apiResults;
        this.open();
      }).catch(() => {
        this.close();
      });
    });
  }

  getAPIResults(searchTerm) {
    return fetch(`/search/suggest?q=${searchTerm}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        return resultsMarkup ? resultsMarkup.innerHTML : '';
      })
      .catch((error) => {
        console.error('API search error:', error);
        return '';
      });
  }

  getCSVResults(searchTerm) {
    return new Promise((resolve) => {
      try {

        if (typeof searchProductData === 'function' && window.ProductSearchData && window.ProductSearchDataLoaded) {
          // Get all results but only show first 10 initially
          const allProductResults = searchProductData(searchTerm, 50); // Get more results for pagination
          resolve(this.formatLiveResults(allProductResults, searchTerm));
        } else {
          resolve('');
        }
      } catch (error) {
        resolve('');
      }
    });
  }

  formatLiveResults(liveResults, searchTerm) {
    if (!liveResults || liveResults.length === 0) return '';

    // Store all results for pagination
    this.allLiveResults = liveResults;
    this.currentSearchTerm = searchTerm;
    this.visibleCount = 10; // Show first 10 results

    let html = `
      <div class="predictive-search__result-group">
        <div>
          <h2 class="predictive-search__heading text-body caption-with-letter-spacing bg-gray-100 font-bold text-lg border-t">
            Alle producten
          </h2>
          <div class="predictive-search__results-container">
            <div class="predictive-search__results-list list-unstyled" role="group">
    `;

    // Show only first 10 results initially
    const visibleResults = liveResults.slice(0, this.visibleCount);
    visibleResults.forEach((product, index) => {
      const imageUrl = product.featured_image || '';
      const highlightedTitle = this.highlightSearchTerm(product.title, searchTerm);
      const price = this.formatPrice(product.price);
      const comparePrice = product.compare_at_price ? this.formatPrice(product.compare_at_price) : null;

      html += `
        <div class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="${product.url}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
            ${imageUrl ? `
              <img class="predictive-search__image rounded-md" 
                   src="${imageUrl}" 
                   alt="${product.title}"
                   width="50"
                   height="50"
                   loading="lazy"
                   onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
              <div class="predictive-search__image-container" style="display:none;">
                <div class="predictive-search__image-placeholder"></div>
              </div>
            ` : `
              <div class="predictive-search__image-container">
                <div class="predictive-search__image-placeholder"></div>
              </div>
            `}
            <div class="predictive-search__item-content">
              <p class="predictive-search__item-heading h5">${highlightedTitle}</p>
              <div class="predictive-search__item-price">
                ${comparePrice ? `
                  <span class="predictive-search__compare-price">${comparePrice}</span>
                  <span class="predictive-search__sale-price">${price}</span>
                ` : `
                  <span class="predictive-search__price">${price}</span>
                `}
              </div>
            </div>
          </a>
        </div>
      `;
    });

    // Close the results list and container
    html += `
            </div>
          </div>
        </div>
      </div>
    `;

    // Add "Meer laden" button at the very bottom if there are more results
    if (liveResults.length > this.visibleCount) {
      html += `
      <div class="predictive-search-load-more-wrapper">
        <button type="button" class="predictive-search-load-more-btn" 
                data-action="load-more">
          Meer laden (${liveResults.length - this.visibleCount} meer)
        </button>
      </div>
      `;
    }

    return html;
  }

  // Legacy function for backward compatibility
  formatCSVResults(csvResults) {
    return this.formatLiveResults(csvResults);
  }

  mergeAndDisplayResults(apiResults, csvResults, searchTerm) {
    // Since we have comprehensive JSON results, prioritize those
    let combinedResults = '';

    if (csvResults) {
      // Use CSV results as primary
      combinedResults = csvResults;

      // Add API results (non-products) if any
      if (apiResults) {
        // Extract non-product sections from API results (collections, pages, articles, etc.)
        const apiNonProducts = this.extractNonProductSections(apiResults);
        if (apiNonProducts) {
          combinedResults = apiNonProducts + combinedResults;
        }
      }
    } else {
      // Fallback to API results if no CSV results
      combinedResults = apiResults;
    }

    this.predictiveSearchResults.innerHTML = combinedResults;

    // Load images for CSV results after HTML is inserted
    this.loadCSVImages();

    // Add event listener for load more button
    this.addLoadMoreEventListener();

    this.open();
  }

  extractNonProductSections(apiResults) {
    // Extract sections that are not products from API results
    // This includes collections, pages, articles, and search suggestions
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = apiResults;

    // Find all result groups except the one with products
    const resultGroups = tempDiv.querySelectorAll('.predictive-search__result-group');
    let nonProductSections = '';

    resultGroups.forEach(group => {
      const heading = group.querySelector('.predictive-search__heading');
      if (heading && !heading.textContent.includes('Producten')) {
        nonProductSections += group.outerHTML;
      }
    });

    return nonProductSections;
  }

  loadCSVImages() {
    // Find all CSV result image containers and load them
    const csvImageContainers = this.predictiveSearchResults.querySelectorAll('.predictive-search__image-container[data-handle]');

    csvImageContainers.forEach(container => {
      const handle = container.getAttribute('data-handle');
      this.loadProductImage(handle, container);
    });
  }

  async loadProductImage(handle, containerElement) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      const product = await response.json();

      if (product.featured_image) {
        const img = document.createElement('img');
        img.className = 'predictive-search__image rounded-md';
        img.src = product.featured_image;
        img.alt = product.title || '';
        img.width = 50;
        img.height = 50;
        img.loading = 'lazy';

        // Replace placeholder with actual image
        containerElement.innerHTML = '';
        containerElement.appendChild(img);
      }
    } catch (error) {
      // Keep the placeholder
    }
  }

  addLoadMoreEventListener() {
    const loadMoreBtn = this.predictiveSearchResults.querySelector('.predictive-search-load-more-btn');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.loadMoreResults();
      });
    }
  }

  highlightSearchTerm(text, searchTerm) {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark class="search-highlight">$1</mark>');
  }

  formatPrice(price) {
    if (!price) return '';

    // Convert to number if it's a string
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;

    // Format as currency (assuming EUR)
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numPrice);
  }

  loadMoreResults() {
    if (!this.allLiveResults || this.visibleCount >= this.allLiveResults.length) {
      return;
    }

    // Increase visible count by 10
    const previousCount = this.visibleCount;
    this.visibleCount = Math.min(this.visibleCount + 10, this.allLiveResults.length);

    // Get the results list container
    const resultsList = this.predictiveSearchResults.querySelector('.predictive-search__results-list');
    const loadMoreContainer = this.predictiveSearchResults.querySelector('.predictive-search-load-more-wrapper');

    if (!resultsList) {
      return;
    }

    // Add new products to existing list (don't clear existing ones)
    const newProducts = this.allLiveResults.slice(previousCount, this.visibleCount);

    newProducts.forEach((product, index) => {
      const imageUrl = product.featured_image || '';
      const highlightedTitle = this.highlightSearchTerm(product.title, this.currentSearchTerm);
      const price = this.formatPrice(product.price);
      const comparePrice = product.compare_at_price ? this.formatPrice(product.compare_at_price) : null;

      const listItem = document.createElement('div');
      listItem.className = 'predictive-search__list-item';
      listItem.setAttribute('role', 'option');
      listItem.setAttribute('aria-selected', 'false');

      listItem.innerHTML = `
        <a href="${product.url}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
          ${imageUrl ? `
            <img class="predictive-search__image rounded-md" 
                 src="${imageUrl}" 
                 alt="${product.title}"
                 width="50"
                 height="50"
                 loading="lazy"
                 onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <div class="predictive-search__image-container" style="display:none;">
              <div class="predictive-search__image-placeholder"></div>
            </div>
          ` : `
            <div class="predictive-search__image-container">
              <div class="predictive-search__image-placeholder"></div>
            </div>
          `}
          <div class="predictive-search__item-content">
            <p class="predictive-search__item-heading h5">${highlightedTitle}</p>
            <div class="predictive-search__item-price">
              ${comparePrice ? `
                <span class="predictive-search__compare-price">${comparePrice}</span>
                <span class="predictive-search__sale-price">${price}</span>
              ` : `
                <span class="predictive-search__price">${price}</span>
              `}
            </div>
          </div>
        </a>
      `;

      resultsList.appendChild(listItem);
    });

    // Update or remove load more button
    if (this.visibleCount >= this.allLiveResults.length) {
      if (loadMoreContainer) {
        loadMoreContainer.remove();
      }
    } else {
      const remainingCount = this.allLiveResults.length - this.visibleCount;
      if (loadMoreContainer) {
        const button = loadMoreContainer.querySelector('.predictive-search-load-more-btn');
        if (button) {
          button.textContent = `Meer laden (${remainingCount} meer)`;
          this.addLoadMoreEventListener();
        }
      }
    }
  }

  open() {
    this.predictiveSearchResults.style.display = 'block';
    $(".mobile-header .mobile-sidebar .ms-bottom").addClass("predictive-open");
  }

  close() {
    this.predictiveSearchResults.style.display = 'none';
    $(".mobile-header .mobile-sidebar .ms-bottom").removeClass("predictive-open");
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);

document.addEventListener('DOMContentLoaded', function () {
  const predictiveSearchElement = document.querySelector('predictive-search');
  if (predictiveSearchElement) {
    window.predictiveSearchInstance = predictiveSearchElement;
  }
});
